import { Inventory2Outlined, LinkOutlined, VideocamOutlined } from "@mui/icons-material";
import { Box, Grid, Stack, Typography } from "@mui/material";
import { ActivationModelGrid, BoxSelect, ConnectionTypeSelect, FormModalActions, MyModal, TextInput } from "components";
import { ENDPOINTS, filterModelsForUI, normalizeActiveModelMedia } from "config/constants";
import { useFetch } from "hooks";
import useTranslation from "hooks/useTranslation";
import { useSnackbar } from "notistack";
import { FormEventHandler, Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { ActiveModel, ModelTypes, RTSPSources } from "types";
import { cameraModalWidth, fieldSx, iconSx, sectionHintSx, sectionTitleSx } from "./formShared";

const defaultValue = {
  rtsp: "",
  esn: "",
  name: "",
  token: "",
  webHooks: "",
  boxId: ""
};
const defaultValues = {
  ...defaultValue
};
const initState =
  process.env.NODE_ENV === "development"
    ? ({
        ...defaultValues,
        rtsp: "rtsp://helloworld.dev"
      } as typeof defaultValues)
    : defaultValues;

interface IAdd {
  onSuccess?: () => void;
  onClose: () => void;
}

export default function AddCamera({ onSuccess, onClose }: IAdd) {
  const [activeModels, setActiveModels] = useState<ActiveModel[]>([]);
  const [enabledModels, setEnabledModels] = useState<ModelTypes[]>([]);
  const [modelsReady, setModelsReady] = useState(false);

  const t = useTranslation();
  const [rtspSource, setRTSPSource] = useState<RTSPSources>("manual");
  const [state, setState] = useState(initState);
  const [errors, setErrors] = useState(defaultValue);
  const disabledSubmit = useMemo(
    () =>
      !!(
        errors.rtsp ||
        (rtspSource === "manual" && !state.rtsp) ||
        (rtspSource === "acc_token" && !state.token) ||
        !state.boxId ||
        !state.name ||
        activeModels.length === 0
      ),
    [state, errors, rtspSource, activeModels]
  );
  const { isLoading, response, error, callFetch: callFetchPost } = useFetch("POST");
  const { enqueueSnackbar } = useSnackbar();

  const connectionOptions = useMemo(
    () => [
      { value: "manual" as RTSPSources, label: t["Manual"] },
      { value: "virtuose" as RTSPSources, label: "Virtuose" },
      { value: "acc_token" as RTSPSources, label: t["Managed by VXG Cloud"] }
    ],
    [t]
  );

  const onSubmit: FormEventHandler<HTMLFormElement> = useCallback(
    (e) => {
      e.preventDefault();
      if (!state.boxId) {
        enqueueSnackbar(t["No Box Selected"], { variant: "error" });
        return;
      }
      const savingModels = activeModels.filter((item) => enabledModels.includes(item.model));
      if (savingModels.length === 0) {
        enqueueSnackbar(t["At least one activation model is required."], { variant: "error" });
        return;
      }
      const options: Record<string, unknown> = {
        ...state,
        boxId: state.boxId,
        models: savingModels.map((model) => model.model),
        activeModels: savingModels
      };
      if (rtspSource === "manual") {
        options.rtsp = state.rtsp;
      } else if (rtspSource === "acc_token") {
        options.acc_token = state.token;
      }
      if (state.webHooks) {
        options.webHooks = state.webHooks;
      }

      callFetchPost(ENDPOINTS.cameras, {
        body: JSON.stringify(options)
      }).catch(console.error);
    },
    [t, activeModels, enabledModels, state, rtspSource, enqueueSnackbar, callFetchPost]
  );

  const onChangeActiveModels = useCallback((model: ModelTypes, video: boolean, audio: boolean) => {
    const flags = normalizeActiveModelMedia(model, video, audio);
    setActiveModels((prevState) => {
      if (flags.video === false && flags.audio === false) {
        return prevState.filter((elm) => elm?.model !== model);
      }
      const index = prevState.findIndex((elm) => elm?.model === model);
      if (index === -1) {
        return [...prevState, { model, ...flags }];
      }
      return prevState.map((elm, i) => (i === index ? { model, ...flags } : elm));
    });
  }, []);

  useEffect(() => {
    if (response) {
      enqueueSnackbar(response.message || "", { variant: "success" });
      onSuccess?.();
      onClose();
    }
  }, [response, onClose, enqueueSnackbar, onSuccess]);

  useEffect(() => {
    if (error) {
      enqueueSnackbar(error.message || "", { variant: "error" });
      if (error.data?.errors) {
        setErrors((prevState) => ({
          ...prevState,
          ...error.data.errors
        }));
      }
    }
  }, [error, enqueueSnackbar]);

  const renderConnectionFields = () => {
    if (rtspSource === "acc_token") {
      return (
        <TextInput
          value={state.token}
          label={t["Token"]}
          type="text"
          placeholder="**************"
          error={!!errors.token}
          helperText={errors.token}
          startAdornment={<LinkOutlined sx={iconSx} />}
          sx={fieldSx}
          onChange={({ target: { value } }) => {
            setState((prevState) => ({ ...prevState, token: value }));
            setErrors((prevState) => ({ ...prevState, token: "" }));
          }}
          fullWidth
          required
        />
      );
    }

    if (rtspSource === "eagleEyeToken") {
      return (
        <Fragment>
          <TextInput
            value={state.esn}
            label={t["ESN No"]}
            type="text"
            placeholder={t["Enter camera ESN no"]}
            error={!!errors.esn}
            helperText={errors.esn}
            startAdornment={<LinkOutlined sx={iconSx} />}
            sx={fieldSx}
            onChange={({ target: { value } }) => {
              setState((prevState) => ({ ...prevState, esn: value }));
              setErrors((prevState) => ({ ...prevState, esn: "" }));
            }}
            fullWidth
            required
          />
          <TextInput
            value={state.token}
            label={t["Token"]}
            type="text"
            placeholder="**************"
            error={!!errors.token}
            helperText={errors.token}
            startAdornment={<LinkOutlined sx={iconSx} />}
            sx={fieldSx}
            onChange={({ target: { value } }) => {
              setState((prevState) => ({ ...prevState, token: value }));
              setErrors((prevState) => ({ ...prevState, token: "" }));
            }}
            fullWidth
            required
          />
        </Fragment>
      );
    }

    return (
      <TextInput
        value={state.rtsp}
        label={t["RTSP URL"]}
        type="text"
        placeholder={t["rtsp://camera-stream-url"]}
        error={!!errors.rtsp}
        helperText={errors.rtsp}
        startAdornment={<LinkOutlined sx={iconSx} />}
        sx={fieldSx}
        onChange={({ target: { value } }) => {
          setState((prevState) => ({ ...prevState, rtsp: value }));
          setErrors((prevState) => ({ ...prevState, rtsp: "" }));
        }}
        fullWidth
        required
      />
    );
  };

  return (
    <MyModal
      title={t["Add Camera"]}
      subtitle={t["Add Camera description"]}
      open={true}
      setOpen={onClose}
      variant="light"
      width={cameraModalWidth}
    >
      <Box component="form" onSubmit={onSubmit}>
        <Stack spacing={2.5}>
          <ConnectionTypeSelect
            label={t["Connection Type"]}
            value={rtspSource}
            onChange={setRTSPSource}
            options={connectionOptions}
          />

          <Box>
            <Typography sx={sectionTitleSx}>{t["Camera Information"]}</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextInput
                  value={state.name}
                  label={t["Camera Name"]}
                  type="text"
                  placeholder={t["e.g Camera Room 67"]}
                  error={!!errors.name}
                  helperText={errors.name}
                  startAdornment={<VideocamOutlined sx={iconSx} />}
                  sx={fieldSx}
                  onChange={({ target: { value } }) => {
                    setState((prevState) => ({ ...prevState, name: value }));
                    setErrors((prevState) => ({ ...prevState, name: "" }));
                  }}
                  fullWidth
                  required
                  autoFocus
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                {rtspSource === "manual" ? renderConnectionFields() : null}
              </Grid>
              {rtspSource !== "manual" ? (
                <Grid item xs={12}>
                  {renderConnectionFields()}
                </Grid>
              ) : null}
              <Grid item xs={12}>
                <TextInput
                  value={state.webHooks}
                  label={t["Webhook URL Optional"]}
                  type="text"
                  placeholder={t["https://example.com/webhook"]}
                  error={!!errors.webHooks}
                  helperText={errors.webHooks}
                  startAdornment={<LinkOutlined sx={iconSx} />}
                  sx={fieldSx}
                  onChange={({ target: { value } }) => {
                    setState((prevState) => ({ ...prevState, webHooks: value }));
                    setErrors((prevState) => ({ ...prevState, webHooks: "" }));
                  }}
                  fullWidth
                />
              </Grid>
            </Grid>
          </Box>

          {modelsReady ? (
            <ActivationModelGrid
              enabledModels={enabledModels}
              activeModels={activeModels}
              onChange={onChangeActiveModels}
              title={t["Activation Models"]}
              description={t["Activation Models description"]}
              hint={t["At least one activation model is required."]}
              videoLabel={t["Video"]}
              audioLabel={t["Audio"]}
              mediaProcessingLabel={t["Media Processing"]}
              t={t}
            />
          ) : null}

          <Box>
            <Typography sx={sectionTitleSx}>{t["Surveillance Box"]}</Typography>
            <BoxSelect
              value={state.boxId}
              hideLabel
              startAdornment={<Inventory2Outlined sx={iconSx} />}
              onChange={(boxId) => setState((prevState) => ({ ...prevState, boxId: boxId ? boxId : "" }))}
              onBox={(box) => {
                if (!box?.site) {
                  setModelsReady(false);
                  return;
                }
                const nextModels = filterModelsForUI(box.site.enabledModels);
                setModelsReady(true);
                setEnabledModels(nextModels);
                setActiveModels((prev) => prev.filter((item) => nextModels.includes(item.model)));
              }}
            />
            <Typography sx={sectionHintSx}>{t["Surveillance Box helper"]}</Typography>
          </Box>
        </Stack>

        <FormModalActions
          onCancel={onClose}
          cancelLabel={t["Cancel"]}
          submitLabel={t["Submit"]}
          loading={isLoading}
          disabled={disabledSubmit}
        />
      </Box>
    </MyModal>
  );
}
